import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useDesignStore } from '../../store/designStore';
import type { FabricCanvas } from '../../types/fabric';
import { Skeleton } from '../Skeleton';

interface ImageEffectsToolProps {
  canvas: FabricCanvas | null;
}

async function fetchEffects() {
  const r = await fetch('/api/image-effects');
  const d = await r.json();
  return (d.effects ?? []) as any[];
}

// Standard ColorMatrix presets (offsets normalized 0-1, matching fabric's
// ColorMatrix#applyTo2d which multiplies the offset column by 255) — the same
// preset set shown on fabric.js's own image-filters demo page.
const COLOR_MATRIX_PRESETS: Record<string, number[]> = {
  brownie: [
    0.59970, 0.34553, -0.27082, 0, 0.18606,
    -0.03770, 0.86095, 0.15059, 0, -0.10601,
    0.24113, -0.07441, 0.44972, 0, -0.07270,
    0, 0, 0, 1, 0,
  ],
  vintage: [
    0.62793, 0.32021, -0.03965, 0, 0.03784,
    0.02578, 0.64411, 0.03259, 0, 0.02926,
    0.04660, -0.08512, 0.52416, 0, 0.02023,
    0, 0, 0, 1, 0,
  ],
  kodachrome: [
    1.12855, -0.39674, -0.03993, 0, 0.24991,
    -0.16404, 1.08353, -0.05499, 0, 0.09698,
    -0.16786, -0.56034, 1.60149, 0, 0.13972,
    0, 0, 0, 1, 0,
  ],
  technicolor: [
    1.91252, -0.85453, -0.09155, 0, 0.04624,
    -0.30878, 1.76589, -0.10601, 0, -0.27589,
    -0.23110, -0.75018, 1.84759, 0, 0.12137,
    0, 0, 0, 1, 0,
  ],
  polaroid: [
    1.438, -0.062, -0.062, 0, 0,
    -0.122, 1.378, -0.122, 0, 0,
    -0.016, -0.016, 1.483, 0, 0,
    0, 0, 0, 1, 0,
  ],
};

// 3x3 Convolute kernels (html5rocks-style, same as fabric.js's documented examples).
const CONVOLUTE_PRESETS: Record<string, number[]> = {
  sharpen: [0, -1, 0, -1, 5, -1, 0, -1, 0],
  emboss: [1, 1, 1, 1, 0.7, -1, -1, -1, -1],
};

// Maps the library's free-text `value` field (e.g. "grayscale", "sepia") to a
// Fabric.js filter instance. `defaultValue` supplies the filter's numeric param
// where one applies (blur radius, brightness/contrast/saturation amount,
// hue rotation in radians).
function buildFilter(fabric: any, value: string, defaultValue: string | null): any {
  const num = defaultValue ? parseFloat(defaultValue) : undefined;
  const key = (value ?? '').trim().toLowerCase();
  switch (key) {
    case 'grayscale':
    // "Black & White" is intentionally an alias for plain Grayscale here — the
    // standard high-contrast threshold ColorMatrix preset for "blackwhite"
    // clips most midtones to pure white on normal photos, which isn't what
    // admins mean by "Black & White" in practice.
    case 'blackwhite': return new fabric.Image.filters.Grayscale();
    case 'sepia':      return new fabric.Image.filters.Sepia();
    case 'invert':     return new fabric.Image.filters.Invert();
    case 'blur':       return new fabric.Image.filters.Blur({ blur: num ?? 0.1 });
    case 'brightness': return new fabric.Image.filters.Brightness({ brightness: num ?? 0.1 });
    case 'contrast':   return new fabric.Image.filters.Contrast({ contrast: num ?? 0.1 });
    case 'saturation': return new fabric.Image.filters.Saturation({ saturation: num ?? 0.1 });
    case 'pixelate':   return new fabric.Image.filters.Pixelate({ blocksize: num ?? 4 });
    case 'noise':      return new fabric.Image.filters.Noise({ noise: num ?? 100 });
    case 'hue':        return new fabric.Image.filters.HueRotation({ rotation: num ?? 0.5 });
    case 'sharpen':
    case 'emboss':     return new fabric.Image.filters.Convolute({ matrix: CONVOLUTE_PRESETS[key] });
    default:
      if (COLOR_MATRIX_PRESETS[key]) return new fabric.Image.filters.ColorMatrix({ matrix: COLOR_MATRIX_PRESETS[key] });
      return null;
  }
}

export function ImageEffectsTool({ canvas }: ImageEffectsToolProps) {
  const { t } = useTranslation();
  const selectedObject = useDesignStore(s => s.selectedObject) as any;
  const { data: effects = [], isLoading } = useQuery({ queryKey: ['image-effects'], queryFn: fetchEffects, staleTime: 300_000 });

  const isImage = selectedObject?.type === 'image';

  const applyEffect = async (effect: any) => {
    if (!canvas || !isImage) return;
    const { fabric } = await import('fabric');
    const filter = buildFilter(fabric, effect.value, effect.defaultValue);
    if (!filter) return;
    selectedObject.filters = [filter];
    selectedObject.applyFilters();
    canvas.renderAll();
  };

  const clearEffects = () => {
    if (!canvas || !isImage) return;
    selectedObject.filters = [];
    selectedObject.applyFilters();
    canvas.renderAll();
  };

  return (
    <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {!isImage && (
        <p style={{ fontSize: 12, color: 'var(--text-muted, #94a3b8)', padding: 8 }}>
          {t('imageEffects.selectImageFirst')}
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, opacity: isImage ? 1 : 0.5 }}>
        {effects.map((effect: any) => (
          <button
            key={effect.id}
            onClick={() => applyEffect(effect)}
            disabled={!isImage}
            title={effect.effectName}
            style={{ border: '1px solid var(--border)', borderRadius: 4, background: 'none', cursor: isImage ? 'pointer' : 'not-allowed', padding: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            {effect.effectImage ? (
              <img src={effect.effectImage} alt={effect.effectName} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 2 }} loading="lazy" />
            ) : (
              <div style={{ width: '100%', aspectRatio: '1', background: '#f1f5f9', borderRadius: 2 }} />
            )}
            <span style={{ fontSize: 12, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{effect.effectName}</span>
          </button>
        ))}
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <div key={`skeleton-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 4 }}>
              <Skeleton style={{ width: '100%', aspectRatio: '1', borderRadius: 2 }} />
            </div>
          ))
        }
      </div>

      {!isLoading && effects.length === 0 && (
        <p style={{ fontSize: 12, color: 'var(--text-muted, #94a3b8)', textAlign: 'center', padding: '16px 0' }}>
          {t('imageEffects.empty')}
        </p>
      )}

      <button onClick={clearEffects} disabled={!isImage} className="btn btn-secondary" style={{ width: '100%', fontSize: 12 }}>
        {t('imageEffects.clear')}
      </button>
    </div>
  );
}
